import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useBack } from '../hooks/useBack';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import { unwrapArray } from '../lib/api-envelope';
import { distinctContactCandidates } from '../lib/contact-candidates';
import { CONTACT_ROLE_OPTIONS } from '../lib/contact-roles';
import { resolveServicesForCopy, serviceEntriesForWrite, serviceNames } from '../lib/service-entries';
import { compareServiceTypes, toServiceType, type ServiceType } from '../types/service-type';
import { ArrowLeft, ChevronDown, ChevronUp, Loader, Copy, Plus, X, Phone, Mail } from 'lucide-react';
import CasingStringSection from '../components/casing/CasingStringSection';
import {
  blankCasingString,
  casingStringsForSave,
  nextCasingStringNumber,
  renumberForNewJob,
  casingStringsSnapshot,
  findCasingStringViolation,
  hydrateCasingStrings,
  MAX_CASING_STRINGS,
  type CasingStringForm,
} from '../lib/casing-strings-form';
import ProvinceSelect from '../components/ProvinceSelect';
import DatePicker from '../components/DatePicker';
import TimePicker from '../components/TimePicker';
import TopDriveThreadSelect from '../components/TopDriveThreadSelect';
import RadioGroup from '../components/ui/RadioGroup';
import { JOB_TYPE_OPTIONS } from '../constants/job-types';
import { rateSheetsForCustomer, inheritedRateSheetLabel } from '../lib/rate-sheet-resolution';
import {
  EMPTY_JOB_CONTACT,
  jobContactFrom,
  jobContactPayloadKeys,
  type JobContactFields,
} from '../lib/job-contact-payload';
import { casingStringHasMetresMessage } from '../lib/casing-strings-display';
import { Tooltip } from '../components/ui/tooltip';
import { toast } from 'sonner';
import { formatCalendarDate } from '../lib/calendar-date';
import PhaseStartNotice from '../components/jobs/PhaseStartNotice';
import type { JobEditWarning } from '../types/dispatch';

// ─── Helpers ────────────────────────────────────────────────────────────────

/** Split any date/datetime value → { date: "2026-03-18", time: "06:00" }
 *  Handles: ISO strings, Date objects, pg text format ("2026-03-18 06:00:00+00") */
function splitDateTime(dt: any): { date: string; time: string } {
  if (!dt) return { date: '', time: '' };
  // Convert anything to a parseable string
  const s = (typeof dt === 'object' && dt instanceof Date)
    ? dt.toISOString()
    : String(dt);
  // Try to extract YYYY-MM-DD and HH:MM from any format
  const match = s.match(/^(\d{4}-\d{2}-\d{2})[T\s](\d{2}:\d{2})/);
  if (match) return { date: match[1], time: match[2] };
  // Date-only
  const dateOnly = s.match(/^(\d{4}-\d{2}-\d{2})/);
  if (dateOnly) return { date: dateOnly[1], time: '' };
  return { date: '', time: '' };
}

/** Combine date + time → "2026-03-18T06:00" or just "2026-03-18" */
function combineDateTime(date: string, time: string): string {
  if (!date) return '';
  if (!time) return date;
  return `${date}T${time}`;
}

// ─── Casing Reference Data ──────────────────────────────────────────────────
// CASING_SIZES / TUBING_SIZES live in ../data/casingData. The weight table,
// the grade list, THREAD_TYPES and weightOptionsFor moved to
// ../lib/casing-strings-form so CasingStringSection can reach them without
// importing this screen. Content unchanged by that move.

const BALE_OPTIONS = [
  { value: 'take_ours', label: 'Take Ours' },
  { value: 'rig_has_own', label: 'Rig Has Own' },
  { value: 'not_required', label: 'Not Required' },
];

/**
 * Services offered on a job come from the per-tenant catalog at
 * `GET /service-types` — see the Services Required section below. They used to
 * be a module-level constant derived from LINE_ITEM_TYPE_LABELS plus a literal
 * `HYC Elevators`; that list is gone, but the two facts it documented still
 * hold and still matter:
 *
 *   - `jobs.services_required` stores DISPLAY STRINGS, not `line_item_type`
 *     values. Do not repoint it at the enum.
 *   - A label a job already holds may not be in the catalog at all. Editing an
 *     old job must not drop it.
 */

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Format a string of digits as (XXX)XXX-XXXX */
function formatPhoneNumber(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 10);
  if (digits.length <= 3) return digits.length ? `(${digits}` : '';
  if (digits.length <= 6) return `(${digits.slice(0, 3)})${digits.slice(3)}`;
  return `(${digits.slice(0, 3)})${digits.slice(3, 6)}-${digits.slice(6)}`;
}

// ─── Section Wrapper ─────────────────────────────────────────────────────────

function Section({ title, defaultOpen = true, children }: { title: string; defaultOpen?: boolean; children: React.ReactNode }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-fieldiq-border rounded-lg">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={`w-full flex items-center justify-between px-4 py-3 bg-fieldiq-card text-sm font-semibold uppercase tracking-wider text-white ${open ? 'rounded-t-lg' : 'rounded-lg'}`}
      >
        {title}
        {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>
      {open && <div className="px-4 py-4 space-y-4 bg-fieldiq-dark rounded-b-lg">{children}</div>}
    </div>
  );
}

// ─── Radio / Toggle Components ───────────────────────────────────────────────

function ToggleGroup({ options, value, onChange }: { options: { value: string; label: string }[]; value: string; onChange: (v: string) => void }) {
  return (
    <div className="flex rounded-lg border border-fieldiq-border overflow-hidden">
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          onClick={() => onChange(opt.value)}
          className={`flex-1 px-4 py-2.5 text-sm font-medium transition-colors ${
            value === opt.value
              ? 'bg-fieldiq-accent text-fieldiq-dark'
              : 'bg-fieldiq-card text-white hover:text-white'
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────

/**
 * The contact's email, or an em dash when there is none.
 *
 * A field ticket's approval goes to the company man on site, so this address is
 * the one the whole approval path depends on — and it was rendered NOWHERE. The
 * selector showed name and phone, the dropdown rows showed name and phone, and
 * a job could carry an address (Smith J-0067, `Tester@tester.com`) that no
 * screen could display. That is what made a captured email read as a form that
 * never asked for one.
 *
 * Absent renders as an em dash, in a slot that is always present, per the
 * repo's absent-is-not-zero rule. Two spellings of "no email" are both wrong
 * here and for the same reason: an omitted line reads as "this contact needs no
 * email", and a blank one reads as a rendering fault. The office is looking at
 * this control to decide whether the ticket can be sent at all.
 */
const EM_DASH = '—';

function contactEmailLabel(email: string | null | undefined): string {
  return email?.trim() ? email : EM_DASH;
}

/**
 * PUT /jobs/:id's `warning`, as one line. Built from the API's two days rather
 * than its `message`, which runs to two sentences; the API's message is the
 * fallback if a day is missing.
 */
function jobEditWarningLine(w: JobEditWarning): string {
  if (w.code === 'run_start_on_other_day' && w.run_started_day) {
    const day = formatCalendarDate(w.run_started_day, { month: 'short', day: 'numeric' });
    return `Run started ${day} — the board keeps it on that day.`;
  }
  return w.message;
}

/** Module scope, so useBack's callback is not rebuilt every render. */
const JOB_BACK_STATE = { from: '/jobs' };

export default function NewJobScreen() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { jobId } = useParams<{ jobId: string }>();
  const isEditMode = !!jobId;
  // "Create job" from an opportunity (CRM-FE-3) opens this form with
  // ?customer=&opportunity=. The customer is a starting value; the opportunity
  // rides on the create as `opportunityId` (POST /jobs, camelCase), and the API
  // refuses it for a different customer.
  const [searchParams] = useSearchParams();
  const opportunityParam = isEditMode ? null : searchParams.get('opportunity');
  const customerParam = isEditMode ? null : searchParams.get('customer');
  /*
   * Save and Back both go BACK one entry, to wherever the form was opened from
   * (the job, almost always). Saving used to replace this entry with the job,
   * which left history list → job → job: the first Back after a save showed the
   * same job again. Opened directly, there is nothing behind it and the
   * fallback replaces this entry with the job (or the list, for a new job).
   */
  const goBack = useBack(isEditMode ? `/jobs/${jobId}` : '/jobs', isEditMode ? { state: JOB_BACK_STATE } : undefined);

  const editPopulatedRef = useRef<string | null>(null);
  // The status this form was LOADED with. An edit sends `status` only when the
  // user changed it: PUT /jobs/:id is partial, and echoing the loaded value
  // back overwrites whatever the field or the board set while the form was
  // open — TNT J-4372 went active -> pending that way, a hand having started
  // it after the office opened the form.
  const loadedStatusRef = useRef<string | null>(null);
  // The contact this form was LOADED with. Empty on a create, which is what
  // makes "omit rather than null" correct there — a job that does not exist yet
  // has no contact to delete. See lib/job-contact-payload.ts.
  const loadedContactRef = useRef<JobContactFields>(EMPTY_JOB_CONTACT);
  // The casing strings as LOADED, in save form, so an edit that leaves casing
  // alone sends no `casingStrings` key and cannot undo a field correction made
  // while this form was open. See casingStringsForSave.
  const loadedCasingRef = useRef<string | null>(null);
  // Every string number this form session has held — loaded, added, or removed
  // since. A new string is numbered above all of them, so a removed number is
  // never reused (see nextCasingStringNumber for the ticket metres that protects).
  const seenStringNumbersRef = useRef<Set<number>>(new Set([1]));

  // Contact dropdown state
  const [contactMode, setContactMode] = useState<'select' | 'new'>('select');
  const [contactDropdownOpen, setContactDropdownOpen] = useState(false);
  const contactDropdownRef = useRef<HTMLDivElement>(null);

  // Form state
  const [form, setForm] = useState({
    jobNumber: '',
    status: 'pending',
    rigName: '',
    wellName: '',
    wellIdLsd: '',
    afeNumber: '',
    wellLicence: '',
    customerId: customerParam ?? '',
    // jobs.rate_sheet_id (0332). '' means NOT CHOSEN — the API resolver falls
    // through to the client's own sheets, then their default, then a sole
    // company book, then asks. Never stamped with the client default: a copy
    // would go stale the moment that default changed, and '' already means
    // inherit.
    rateSheetId: '',
    customerContactName: '',
    customerContactPhone: '',
    customerContactEmail: '',
    // Recorded, not consulted. Nothing resolves on role — see the note on the
    // select below. It is captured here because the moment the office types
    // the contact is the moment they know who the person is; asking later
    // means asking someone who was not there.
    customerContactRole: '',
    /**
     * "Use this address for this job's approval emails" — the checkbox below
     * Contact Email.
     *
     * An ACTION, not a mirror of stored state: ticked, it writes the contact
     * address to this job's `rig_approval_email`; unticked, it says NOTHING and
     * the payload omits the key entirely. It can set and can never clear, which
     * is what makes it safe to default off on an edit — see the payload note.
     *
     * DEFAULT UNCHECKED, deliberately opposite to the same-worded checkbox on the
     * ticket send modal, which defaults CHECKED. At send time the office has
     * just typed the address they are sending to. At booking they are recording
     * a contact and may not yet know whose address it is.
     */
    saveAsRigEmail: false,
    locationType: 'surface',
    locationCity: '',
    locationProvinceState: '',
    locationLsd: '',
    directions: '',
    radioFrequency: '',
    scheduledDate: '',
    scheduledTime: '',
    dateTimeWash: '',
    washTime: '',
    jobType: '',
    /**
     * The job's casing strings, in ordinal order — one entry per string, N of
     * them. Replaces the twenty-five flat `casing*` / `casing*2` keys this form
     * used to carry, which is why no tenant could enter a third string: there
     * was no third set to put it in.
     *
     * `string_number` is deliberately NOT stored here. It is derived from array
     * position at save time, so removing the middle of three renumbers the
     * survivors 1 and 2 by construction rather than by remembering to.
     */
    casingStrings: [blankCasingString(1)] as CasingStringForm[],
    threadProtectors: false,
    topDriveThread: '',
    topDriveThreadOther: '',
    baleExtensions: '',
    spider: '',
    depthMetres: '',
    depthFeet: '',
    servicesRequired: [] as string[],
    specialInstructions: '',
  });

  const [error, setError] = useState('');
  const [clonedFromJobId, setClonedFromJobId] = useState<string | null>(null);

  // New customer inline form state
  const [showNewCustomer, setShowNewCustomer] = useState(false);
  const [newCustomer, setNewCustomer] = useState({ name: '', mainContactName: '', mainContactPhone: '' });

  // Rig autocomplete state
  const [rigInputFocused, setRigInputFocused] = useState(false);
  const [rigSourceJob, setRigSourceJob] = useState<any>(null);
  const [showDuplicatePrompt, setShowDuplicatePrompt] = useState(false);
  const rigDropdownRef = useRef<HTMLDivElement>(null);

  // Customers query
  const { data: customers = [], isSuccess: customersLoaded } = useQuery({
    queryKey: ['customers'],
    queryFn: () => api.customers.list().then((r) => {
      const d = r.data;
      return Array.isArray(d) ? d : (d?.data ?? d?.customers ?? []);
    }),
  });

  /**
   * The tenant's service catalog, for the Services Required checkboxes.
   *
   * Deliberately NOT given a placeholder or an `initialData: []`. An empty
   * checkbox list is indistinguishable from "this tenant offers no services",
   * and a user who saves a job while looking at one silently clears their
   * selections. The section reads `servicesLoading` / `servicesError` and
   * refuses to present an editable-but-empty list — see below.
   */
  const {
    data: serviceCatalog = [],
    isLoading: servicesLoading,
    isError: servicesError,
    refetch: refetchServices,
    isFetching: servicesFetching,
  } = useQuery<ServiceType[]>({
    // ALL rows, inactive included, under its own key: ServicesPage caches the
    // active-only list under ['service-types'] and must not be handed this
    // one. Inactive rows are needed here so a stored { id } on a job whose
    // service the tenant has since retired still resolves to its row — for
    // the duplicate path and for sending the id back on save. The picker
    // itself still offers active rows only (`catalogServices` below).
    queryKey: ['service-types', 'all'],
    queryFn: () =>
      api.serviceTypes.list({ includeInactive: true }).then((r) =>
        unwrapArray<any>(r.data)
          .map(toServiceType)
          .filter((s): s is ServiceType => s !== null)
      ),
  });

  /** Active catalog labels, in catalog order. */
  const catalogServices = React.useMemo(
    () =>
      serviceCatalog
        .filter((s) => s.isActive)
        .sort(compareServiceTypes)
        .map((s) => s.name),
    [serviceCatalog]
  );

  /**
   * Only once the catalog has actually arrived can we say a stored label is no
   * longer offered. While loading, or after a failed fetch, every stored label
   * would look absent from an empty catalog — labelling them all "No longer
   * offered" would be a fabrication, not a fallback.
   */
  const catalogLoaded = !servicesLoading && !servicesError;

  /**
   * Every off-catalog label this edit session has shown, checked or not.
   *
   * Without it the union is computed against the CURRENT selection, so
   * unchecking a legacy label removes it from the union and the row vanishes
   * mid-click — the user cannot undo their own uncheck, because nothing else on
   * the screen can reproduce a string the catalog does not contain. The row has
   * to outlive the checkbox state for as long as the form is open.
   */
  const [seenServices, setSeenServices] = useState<string[]>([]);
  useEffect(() => {
    setSeenServices((prev) => {
      const next = prev.concat(form.servicesRequired.filter((s) => !prev.includes(s)));
      return next.length === prev.length ? prev : next;
    });
  }, [form.servicesRequired]);

  /**
   * The rendered checkbox set: (active catalog) ∪ (labels this job has held).
   *
   * The union is the whole point. `services_required` holds free display
   * strings, and 151 array entries across 150 jobs use a vocabulary the catalog
   * deliberately does not contain ("Torque Monitoring (Computers)", "Casing
   * Running Tool (CRT)", "Vacuum Drifting", a stray "Vacuum"/"Drifting" split,
   * a junk "Production"). Those render checked and stay checked on save unless
   * the user unchecks them. A stored value is never dropped silently.
   */
  const visibleServices = React.useMemo(() => {
    // `seenServices` leads because it is insertion-ordered and append-only: a
    // row keeps its position for the whole session. Ordering the current
    // selection first instead makes a row JUMP down the list on the click that
    // unchecks it, which reads as "it disappeared" and invites a second click.
    //
    // The current selection is still unioned in directly — `seenServices` is
    // set from an effect, one render later, and a stored label must not flicker
    // out of the list on the frame the job's own values arrive.
    const extras = seenServices.concat(
      form.servicesRequired.filter((s) => !seenServices.includes(s))
    );
    return [...catalogServices, ...extras.filter((s) => !catalogServices.includes(s))];
  }, [catalogServices, form.servicesRequired, seenServices]);

  // Fetch existing job for edit mode
  const { data: editJobData, isLoading: editLoading } = useQuery({
    queryKey: ['job', jobId],
    queryFn: () => api.jobs.get(jobId!).then(r => r.data.data),
    enabled: isEditMode,
  });

  // Archived-client backstop: a soft-deleted client won't be in the active list,
  // which would leave the controlled Client <select> showing a blank placeholder
  // even though the job still points at that client. Detect that case and fetch
  // the archived client directly (the /customers/:id endpoint returns it regardless
  // of deleted_at) so we can show its name — flagged — and warn the user to reselect.
  // Rate sheets for the picker. `GET /rate-sheets` returns every active sheet in
  // the company; scoping to this client happens in `rateSheetsForCustomer`,
  // which WIDENS (their contracts PLUS the company books) rather than narrowing.
  const { data: rateSheets = [] } = useQuery({
    queryKey: ['rate-sheets'],
    queryFn: () => api.rateSheets.list().then((r) => {
      const body = r.data;
      const rows = 'data' in body ? body.data : body;
      return Array.isArray(rows) ? rows : [];
    }),
  });

  // The selected client's stored default, shown as the "not set" label. Read
  // from the client record rather than derived: predicting which sheet wins
  // would be a second copy of the API's precedence chain living in the browser.
  const selectedCustomer = customers.find((c: any) => c.id === form.customerId);
  const customerDefaultRateSheetId =
    selectedCustomer?.defaultRateSheetId ?? selectedCustomer?.default_rate_sheet_id ?? null;

  const { contracts: clientSheets, books: companyBooks } =
    rateSheetsForCustomer(rateSheets as any[], form.customerId || null);

  const customerInList = !!form.customerId && customers.some((c: any) => c.id === form.customerId);
  const needArchivedFetch = !!form.customerId && customersLoaded && !customerInList;
  const { data: archivedCustomer } = useQuery({
    queryKey: ['customer-archived', form.customerId],
    queryFn: () => api.customers.get(form.customerId).then((r) => {
      const d = r.data?.data ?? r.data;
      return d ? { ...d, _archived: true } : null;
    }),
    enabled: needArchivedFetch,
  });
  // Only surface the backstop once we've confirmed the client is missing from the
  // active list AND the archived fetch resolved — avoids a flash during load.
  const showArchivedClient = needArchivedFetch && !!archivedCustomer;

  // Populate form when editing an existing job
  useEffect(() => {
    if (!isEditMode || !editJobData || editPopulatedRef.current === jobId) return;
    editPopulatedRef.current = jobId!;

    const src = editJobData;

    // ONE hydration rule for all N strings, shared with the duplicate path below.
    // Reads `casingStrings` — the array the step 7 API returns — and falls back
    // to the twelve flat columns only for a payload that predates it. The two
    // sites used to carry separate copies of this logic, and they had already
    // drifted: this one read both key spellings, the duplicate path read only
    // camelCase.
    const casingStrings = hydrateCasingStrings(src);
    loadedCasingRef.current = casingStringsSnapshot(casingStrings);
    seenStringNumbersRef.current = new Set(
      casingStrings.map((cs) => cs.stringNumber).filter((n): n is number => n !== null),
    );

    // Names only, whatever shape the API stored: the legacy bare strings or
    // the { id, name } objects the column is moving to. The checkbox set
    // compares and renders on names — see lib/service-entries.ts.
    const servicesRaw = serviceNames(src.services_required ?? src.servicesRequired);

    // The contact as STORED, kept so the save can tell "never had one" from
    // "the user just cleared it". See lib/job-contact-payload.ts — sending
    // `|| null` for both is what turned a blank form into a server-side wipe.
    const loadedContact = jobContactFrom(src);
    loadedContactRef.current = loadedContact;

    loadedStatusRef.current = src.status || 'pending';

    setForm({
      jobNumber: src.job_number || src.jobNumber || '',
      status: src.status || 'pending',
      rigName: src.rig_name || src.rigName || '',
      wellName: src.well_name || src.wellName || '',
      wellIdLsd: src.well_id_lsd || src.wellIdLsd || '',
      afeNumber: src.afe_number || src.afeNumber || '',
      wellLicence: src.well_licence || src.wellLicence || '',
      customerId: src.customer_id || src.customerId || '',
      // Both spellings are real and neither is a fallback for a missing API:
      // GET /jobs/:id is a raw `SELECT j.*` and answers snake_case, while
      // POST/PUT return Drizzle's `.returning()` and answer camelCase. `??`
      // rather than `||` so a future empty-string does not silently fall
      // through to the other key.
      rateSheetId: src.rateSheetId ?? src.rate_sheet_id ?? '',
      customerContactName: loadedContact.name,
      customerContactPhone: loadedContact.phone,
      customerContactEmail: loadedContact.email,
      // BLANK ON PURPOSE, and it is not a dropped field. `role` lives on
      // customer_contacts, not on the job — there is nothing on this row to
      // hydrate it from. A blank going back up means "the form did not say",
      // which upsertJobContact treats as leave-alone, so an existing role
      // survives an edit that never mentions it. Filling it would need a second
      // fetch keyed on (client, rig, name), which is the contacts query the
      // auto-fill already runs; wiring the two together is a real improvement
      // and is not this PR.
      customerContactRole: '',
      // FALSE on load, even when the job already HAS a rig_approval_email. The
      // box is an action ("also save this one"), not a display of stored state,
      // and it can only set — so an unticked box on an edit changes nothing.
      //
      // KNOWN AMBIGUITY, FILED — see the API's
      // docs/rig-approval-email-send-door-scope.md, "Limit 1". On such a job an
      // unticked box means "no change"; it does NOT mean "this job has no
      // rig approval email", and the two are indistinguishable from this form.
      // Resolving it means SHOWING the stored address, which is already on the
      // GET /jobs/:id payload as `rig_approval_email` — nothing is owed by the
      // API, the work is display scope this control was not given. The filing
      // also records why the display cannot ship on its own: once the address is
      // visible the office will expect to CLEAR it from here, and a set-only
      // control has no room for that. The send modal is where a rig address is
      // currently read and changed.
      saveAsRigEmail: false,
      locationType: src.location_type || src.locationType || 'surface',
      locationCity: src.location_city || src.locationCity || '',
      locationProvinceState: src.location_province_state || src.locationProvinceState || '',
      locationLsd: src.location_lsd || src.locationLsd || '',
      directions: src.directions || '',
      radioFrequency: src.radio_frequency || src.radioFrequency || '',
      scheduledDate: splitDateTime(src.scheduled_date || src.scheduledDate || '').date,
      scheduledTime: splitDateTime(src.scheduled_date || src.scheduledDate || '').time,
      dateTimeWash: splitDateTime(src.date_time_wash || src.dateTimeWash || '').date,
      washTime: splitDateTime(src.date_time_wash || src.dateTimeWash || '').time,
      jobType: src.job_type || src.jobType || '',
      casingStrings,
      threadProtectors: src.thread_protectors ?? src.threadProtectors ?? false,
      topDriveThread: src.top_drive_thread || src.topDriveThread || '',
      topDriveThreadOther: '',
      baleExtensions: src.bale_extensions || src.baleExtensions || '',
      spider: src.spider || '',
      depthMetres: (src.depth_metres ?? src.depthMetres ?? '').toString(),
      depthFeet: (src.depth_feet ?? src.depthFeet ?? '').toString(),
      servicesRequired: servicesRaw,
      specialInstructions: src.special_instructions || src.specialInstructions || '',
    });
  }, [isEditMode, editJobData]);


  // Per-client contacts query — scoped by rig when entered
  const { data: clientContacts = [], isSuccess: contactsLoaded } = useQuery<{ id: string; name: string; phone: string; email: string; role: string; isPrimary: boolean }[]>({
    queryKey: ['customer-contacts', form.customerId, form.rigName || null],
    queryFn: () => api.customers.listContacts(form.customerId, form.rigName || undefined).then((r) => {
      const d = r.data;
      return Array.isArray(d) ? d : (d?.data ?? d?.contacts ?? []);
    }),
    enabled: !!form.customerId,
  });

  // Auto-fill the contact only when the rig has ONE distinct candidate.
  //
  // This used to count ROWS — `clientContacts.length !== 1` — and four of the
  // six addressed rigs in production that hold several rows hold ONE SHARED RIG
  // MAILBOX with several named hands attached. On those there was never
  // anything to disambiguate: every choice leads to the same address. The
  // refusal was invented by counting the wrong thing, and the note on
  // `changeRigFromUser` below already measured what it cost — 29% of contacts
  // survived a rig change on clients with three contacts, against 85% on
  // clients with one.
  //
  // `distinctContactCandidates` collapses rows sharing a normalised address and
  // leaves addressless rows alone (two hands with no email are two people, not
  // one). A collapsed group contributes only the fields it AGREES on, so a
  // shared mailbox fills the address and leaves the name for the office rather
  // than picking one of three hands at random. See src/lib/contact-candidates.ts.
  const contactCandidates = useMemo(
    () => distinctContactCandidates(clientContacts),
    [clientContacts]
  );

  const contactAutoFilledRef = useRef<string | null>(null);
  useEffect(() => {
    const scopeKey = `${form.customerId}|${form.rigName}`;
    if (!form.customerId) return;
    if (contactAutoFilledRef.current === scopeKey) return;

    // WAIT FOR THE CONTACTS QUERY, then stamp the ref.
    //
    // Without this the auto-fill never fires at all — measured against
    // origin/main, with exactly ONE saved contact, which is the only case the
    // old row-counting rule ever accepted. Selecting a client sets `customerId`
    // and nulls the ref in one handler; the effect then runs IMMEDIATELY, while
    // the (client, rig) contacts query is still in flight and `clientContacts`
    // is still the `= []` default. It stamped the ref on that pass, found no
    // candidates, and returned — so when the contacts actually arrived the
    // effect re-ran and took the `already done` early return. The ref recorded
    // an auto-fill that had never happened.
    //
    // `isSuccess` is per queryKey, so it goes false again on a client or rig
    // change and this waits afresh each time.
    if (!contactsLoaded) return;
    contactAutoFilledRef.current = scopeKey;
    // One CANDIDATE, not one row. More than one means genuinely different
    // addresses (or addressless strangers) — let the user pick.
    if (contactCandidates.length !== 1) return;
    // Don't overwrite manually entered values
    if (form.customerContactName.trim()) return;
    const contact = contactCandidates[0];
    set('customerContactName', contact.name || '');
    set('customerContactPhone', contact.phone || '');
    set('customerContactEmail', contact.email || '');
    set('customerContactRole', contact.role || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactCandidates, contactsLoaded, form.customerId, form.rigName]);

  /**
   * The rig changed BECAUSE THE USER CHANGED IT — so the contact, which is
   * scoped to (client, rig), no longer applies and is cleared for reselection.
   *
   * This used to be a `useEffect` on `[form.rigName]` comparing against a
   * `useRef(form.rigName)` seeded at MOUNT, when the field is still ''. An
   * effect watching a value cannot see who wrote it, and the first thing to
   * write it on an existing job is the edit hydration below — so loading a job
   * for edit read as a rig change and wiped the three contact fields the
   * hydration had just filled in. The user then saved a form that looked
   * complete and the contact went to NULL on the server.
   *
   * Recovery was invisible too: the auto-fill above only refills when the
   * (client, rig) pair matches EXACTLY ONE saved contact, so it silently failed
   * for every client with several — measured on TNT, 85% of contacts survived
   * where one contact could refill them and 29% where three could not.
   *
   * Moving the clear to the three places a user can actually change the rig
   * makes the bug structurally impossible rather than guarded against: nothing
   * programmatic — hydration, duplicate, a future prefill — routes through here.
   */
  const changeRigFromUser = (nextRigName: string) => {
    if (form.rigName === nextRigName) return;
    setForm((prev) => ({
      ...prev,
      rigName: nextRigName,
      customerContactName: '',
      customerContactPhone: '',
      customerContactEmail: '',
      // The role belongs to the contact, and the contact is scoped to
      // (client, rig). A rig change invalidates it with the other three.
      customerContactRole: '',
      // The checkbox's helper NAMES the rig — "Other jobs on Ensign 144 aren't
      // changed". Changing the rig makes that sentence false, and the address
      // was typed for the old rig, so it cannot survive the change any more
      // than the contact can.
      saveAsRigEmail: false,
    }));
    contactAutoFilledRef.current = null;
    setContactMode('select');
  };

  // Close contact dropdown on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (contactDropdownRef.current && !contactDropdownRef.current.contains(e.target as Node)) {
        setContactDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Rigs query for autocomplete
  const { data: rigsData } = useQuery({
    queryKey: ['rigs'],
    queryFn: () => api.jobs.listRigs(),
  });
  const allRigs = unwrapArray<string>(rigsData?.data);

  // Filter rigs based on current input
  const filteredRigs = form.rigName
    ? allRigs.filter((r) => r.toLowerCase().includes(form.rigName.toLowerCase()))
    : allRigs;

  // Field setter
  const set = (field: string, value: any) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  /**
   * Merge a patch into ONE casing string, found by its stable key.
   *
   * Functional update, deliberately: CasingStringSection's cascades can fire
   * more than one patch in a single commit (a size change both clears the
   * weight and refills the mm), and a non-functional setState would drop all
   * but the last.
   */
  const updateCasingString = (key: string, patch: Partial<CasingStringForm>) =>
    setForm((prev) => ({
      ...prev,
      casingStrings: prev.casingStrings.map((s) => (s.key === key ? { ...s, ...patch } : s)),
    }));

  /**
   * Append an empty string, numbered above every number this form has held —
   * never a removed string's number (see nextCasingStringNumber).
   */
  const addCasingString = () =>
    setForm((prev) => {
      if (prev.casingStrings.length >= MAX_CASING_STRINGS) return prev;
      const n = nextCasingStringNumber(prev.casingStrings, seenStringNumbersRef.current);
      seenStringNumbersRef.current.add(n);
      return { ...prev, casingStrings: [...prev.casingStrings, blankCasingString(n)] };
    });

  /**
   * Remove one string. The survivors KEEP THEIR NUMBERS — removing the middle
   * of three leaves 1 and 3, a gap the API allows. Renumbering them by position
   * wrote a neighbour's spec over the removed row, carrying any ticket metres on
   * it onto a different pipe with no refusal (api#830). Keeping the numbers
   * means the save names the removed string, and the API refuses it by name
   * (CASING_STRING_HAS_METRES) while a ticket holds metres on it.
   *
   * String 1 has no Remove control (see CasingStringSection), and this guard is
   * the second half of that rule: a job with no strings is not a valid casing
   * job, and a form with no sections has nothing to type into.
   */
  const removeCasingString = (key: string) =>
    setForm((prev) =>
      prev.casingStrings.length <= 1
        ? prev
        : { ...prev, casingStrings: prev.casingStrings.filter((s) => s.key !== key) }
    );

  // Metres <-> Feet conversion
  useEffect(() => {
    if (form.depthMetres) {
      const feet = (parseFloat(form.depthMetres) * 3.28084).toFixed(1);
      set('depthFeet', feet);
    } else {
      set('depthFeet', '');
    }
  }, [form.depthMetres]);

  // Close rig dropdown on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (rigDropdownRef.current && !rigDropdownRef.current.contains(e.target as Node)) {
        setRigInputFocused(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Handle rig selection from autocomplete
  const handleRigSelect = async (rigName: string) => {
    changeRigFromUser(rigName);
    setRigInputFocused(false);

    // Skip duplication prompt in edit mode
    if (isEditMode) return;

    try {
      const res = await api.jobs.getLatestByRig(rigName);
      const sourceJob = res.data?.data;
      if (sourceJob) {
        setRigSourceJob(sourceJob);
        setShowDuplicatePrompt(true);
        // Auto-fill rig-specific fields (user can always override)
        setForm(prev => ({
          ...prev,
          topDriveThread: sourceJob.topDriveThread || sourceJob.top_drive_thread || prev.topDriveThread,
          baleExtensions: sourceJob.baleExtensions || sourceJob.bale_extensions || prev.baleExtensions,
          spider: sourceJob.spider || prev.spider,
        }));
      } else {
        setRigSourceJob(null);
        setShowDuplicatePrompt(false);
      }
    } catch {
      setRigSourceJob(null);
      setShowDuplicatePrompt(false);
    }
  };

  // Duplicate last job for this rig
  const handleDuplicate = () => {
    if (!rigSourceJob) return;
    const src = rigSourceJob;

    // The SAME hydration as the edit path. This site used to carry its own copy
    // that read camelCase keys only, so a job whose payload arrived snake_cased
    // duplicated with its casing blank. One function now, and it reads the
    // `casingStrings` array GET /jobs/by-rig returns as of the step 7 API — so a
    // duplicate carries all N strings, not the two the columns could hold.
    // Numbered 1..N: a new job has no rows for a renumber to overwrite.
    const casingStrings = renumberForNewJob(hydrateCasingStrings(src));
    seenStringNumbersRef.current = new Set(casingStrings.map((_, i) => i + 1));

    // Both key spellings, and the EMAIL — which this path silently dropped
    // while the edit path carried it, the same drift the casing comment above
    // records. `changeRigFromUser` is deliberately not called here: a duplicate
    // is a programmatic fill, and routing it through the user-action clear
    // would wipe the contact this very statement is setting whenever the source
    // job's rig string differs from what the user typed.
    const duplicatedContact = jobContactFrom(src);

    setForm((prev) => ({
      ...prev,
      // Keep: status ('pending'), all dates cleared — jobNumber assigned by server on create
      rigName: src.rigName || prev.rigName,
      wellName: src.wellName || '',
      wellIdLsd: src.wellIdLsd || '',
      afeNumber: src.afeNumber || '',
      wellLicence: src.wellLicence || '',
      customerId: src.customerId || '',
      customerContactName: duplicatedContact.name,
      customerContactPhone: duplicatedContact.phone,
      customerContactEmail: duplicatedContact.email,
      // Reset, for the same reason as the edit hydration: the duplicated JOB
      // carries no role, so leaving `...prev` standing would carry whatever
      // role happened to be on the form into a contact copied from elsewhere.
      customerContactRole: '',
      saveAsRigEmail: false,
      locationType: src.locationType || 'surface',
      locationCity: src.locationCity || '',
      locationProvinceState: src.locationProvinceState || '',
      directions: src.directions || '',
      radioFrequency: src.radioFrequency || '',
      // Dates intentionally cleared
      scheduledDate: '',
      scheduledTime: '',
      dateTimeWash: '',
      washTime: '',
      jobType: src.jobType || '',
      casingStrings,
      threadProtectors: src.threadProtectors ?? false,
      topDriveThread: src.topDriveThread || '',
      topDriveThreadOther: '',
      baleExtensions: src.baleExtensions || '',
      spider: src.spider || '',
      depthMetres: src.depthMetres?.toString() || '',
      depthFeet: src.depthFeet?.toString() || '',
      // A duplicate is a NEW job, so its services are resolved against the
      // catalog as it is NOW — by row id where the source entry has one. This
      // path used to copy the array verbatim and was the last writer still
      // minting old spellings (PAR J-0054); see resolveServicesForCopy.
      servicesRequired: resolveServicesForCopy(src.servicesRequired ?? src.services_required, serviceCatalog),
      specialInstructions: src.specialInstructions || '',
    }));

    setClonedFromJobId(src.id);
    setShowDuplicatePrompt(false);
  };

  // Dismiss duplicate prompt — keep form fresh with just rig name
  const handleCreateNew = () => {
    setShowDuplicatePrompt(false);
    setRigSourceJob(null);
  };

  // NOTE: the torque inputs (resolved thread, canonical size, available weights)
  // used to be computed here as two parallel sets — thread1Value/thread2Value,
  // torqueSize1/torqueSize2 — and handed to two <PipeTorqueFields>. They now
  // live inside CasingStringSection, keyed off that string's own state. See the
  // header of that file for why hoisting them back is the TNT J-4290 bug.

  // Create customer mutation
  const createCustomerMutation = useMutation({
    mutationFn: (data: any) => api.customers.create(data),
    onSuccess: (res) => {
      const created = res.data?.data;
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      if (created) {
        set('customerId', created.id);
        if (created.mainContactName) set('customerContactName', created.mainContactName);
        if (created.mainContactPhone) set('customerContactPhone', created.mainContactPhone);
      }
      setShowNewCustomer(false);
      setNewCustomer({ name: '', mainContactName: '', mainContactPhone: '' });
    },
    onError: (err: any) => {
      setError(err.response?.data?.error || 'Failed to create customer');
    },
  });

  const handleSaveNewCustomer = () => {
    if (!newCustomer.name.trim()) {
      setError('Client name is required');
      return;
    }
    setError('');
    createCustomerMutation.mutate(newCustomer);
  };

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data: any) => api.jobs.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      queryClient.invalidateQueries({ queryKey: ['jobs-summary'] });
      // This screen owns scheduled_date, so it owns the dispatch board's view
      // of the schedule. `['dispatch-jobs']` is a DIFFERENT key from `['jobs']`
      // and prefix invalidation does not bridge them — see updateMutation below
      // for what its absence cost.
      queryClient.invalidateQueries({ queryKey: ['dispatch-jobs'] });
    },
    onError: (err: any) => {
      setError(err.response?.data?.error || 'Failed to create job');
    },
  });

  // Update mutation (edit mode)
  const updateMutation = useMutation({
    mutationFn: (data: any) => api.jobs.update(jobId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      queryClient.invalidateQueries({ queryKey: ['jobs-summary'] });
      queryClient.invalidateQueries({ queryKey: ['job', jobId] });
      // THE DISPATCH BOARD READS A DIFFERENT KEY. DispatchScreen's week, month,
      // status and calendar views all read `['dispatch-jobs', queryParams]`
      // (DispatchScreen.tsx:152), and HomeScreen reads `['dispatch-jobs', {}]`.
      // None of them is a descendant of `['jobs']`, so the three lines above
      // leave every one of them holding the pre-edit schedule.
      //
      // What that looked like: re-date a job here, go back to the calendar, and
      // the job is still on its old day. Navigating away and back does not fix
      // it either — `refetchOnMount` defaults to `true` but only refetches when
      // the query is stale, and App.tsx:250 sets `staleTime: 30_000`. So the
      // board stays wrong until 30 seconds have elapsed, which is why this was
      // reported as "you have to navigate away and back TWICE": the second trip
      // works because it takes longer than 30s to make, not because two trips
      // are special. Measured: remount at 15.2s and 18.2s did not refetch, at
      // 77.7s it did.
      //
      // Invalidation is the whole fix — an invalidated query is stale
      // regardless of age, so this refetches immediately with no navigation and
      // no change to the global staleTime.
      queryClient.invalidateQueries({ queryKey: ['dispatch-jobs'] });
    },
    onError: (err: any) => {
      // api#830: removing a string a ticket holds metres on names the tickets.
      setError(casingStringHasMetresMessage(err) ?? (err.response?.data?.error || 'Failed to update job'));
    },
  });

  const activeMutation = isEditMode ? updateMutation : createMutation;

  const handleSave = (targetStatus?: string) => {
    setError('');

    // Build payload
    const resolvedTopDrive = form.topDriveThread;

    // The same bounds the API enforces, checked here so the user gets a
    // sentence naming the string instead of a 400 after the round trip.
    // `maxLength` covers the inputs this screen owns; ConnectionTypeSelect
    // manages its own, so this is the backstop that catches a long thread name.
    const casingViolation = findCasingStringViolation(form.casingStrings);
    if (casingViolation) {
      setError(casingViolation);
      return;
    }

    const payload: Record<string, any> = {
      ...(isEditMode && form.jobNumber ? { jobNumber: form.jobNumber } : {}),
      // A create always states its status. An edit states it only when the
      // select was changed — see loadedStatusRef.
      ...(!isEditMode || form.status !== loadedStatusRef.current
        ? { status: targetStatus || form.status }
        : {}),
      rigName: form.rigName || null,
      wellName: form.wellName || null,
      wellIdLsd: form.wellIdLsd || null,
      afeNumber: form.afeNumber || null,
      wellLicence: form.wellLicence || null,
      customerId: form.customerId || null,
      // '' → null, so clearing the select genuinely means "inherit" rather than
      // leaving the previous choice standing.
      rateSheetId: form.rateSheetId || null,
      // A value when there is one, an explicit null ONLY when the user cleared
      // one that was there, and no key at all otherwise — because PUT /jobs/:id
      // is partial and reads `!== undefined`. `|| null` on all three said
      // "delete the contact" every time the form happened to be blank, which is
      // how a hydration bug two hundred lines up became a server-side wipe.
      ...jobContactPayloadKeys(
        {
          name: form.customerContactName,
          phone: form.customerContactPhone,
          email: form.customerContactEmail,
        },
        loadedContactRef.current
      ),
      // NOT one of the three above, and deliberately not routed through
      // jobContactPayloadKeys. That helper exists because those three map to
      // JOB COLUMNS where `null` means DELETE THE STORED CONTACT, so it has to
      // tell "never entered" from "cleared". `role` maps to no job column at
      // all — the API forwards it to upsertJobContact, which treats a blank as
      // "the form did not say" and leaves any stored role standing. So a bare
      // `|| null` here cannot wipe anything, which is the only reason it is
      // safe to write the shape the other three cannot.
      customerContactRole: form.customerContactRole || null,
      // SET-ONLY, and the omission is the whole safety property.
      //
      // Ticked with an address present, this sends it. Anything else sends NO
      // KEY AT ALL — never `null`. `PUT /jobs/:id` reads `!== undefined`, so an
      // absent key leaves the stored value alone while `null` CLEARS it; with a
      // box that defaults off, sending null would wipe the rig's address on
      // every ordinary edit of a job that had one. Same distinction
      // jobContactPayloadKeys exists for, reached from the other direction:
      // there the helper has to tell "never entered" from "cleared", here the
      // control simply cannot express "cleared" and so never has to.
      //
      // Clearing a rig address is therefore not possible from this form. That
      // is deliberate for now and noted in the PR — the send modal is where a
      // rig address is currently changed.
      ...(form.saveAsRigEmail && form.customerContactEmail.trim()
        ? { rigApprovalEmail: form.customerContactEmail.trim() }
        : {}),
      locationType: form.locationType || null,
      locationCity: form.locationCity || null,
      locationProvinceState: form.locationProvinceState || null,
      directions: form.directions || null,
      radioFrequency: form.radioFrequency || null,
      scheduledDate: combineDateTime(form.scheduledDate, form.scheduledTime) || null,
      dateTimeWash: combineDateTime(form.dateTimeWash, form.washTime) || null,
      // No dateTimeRun. There was never a control for it -- dateTimeWash has a
      // DatePicker/TimePicker pair a few lines down and its sibling had nothing
      // -- so this key only ever carried a value round-tripped from an existing
      // job, and the API dropped the column in 0340. scheduled_date IS the run
      // date; if a run time is ever wanted it reads that field, not a second one
      // that can drift from it.
      jobType: form.jobType || null,
      /**
       * The complete set of casing strings — sent on a create, and on an edit
       * ONLY WHEN IT CHANGED. An unchanged array is omitted so a stale form
       * cannot undo a crew's field correction; see casingStringsForSave. When
       * it is sent, it is the complete set, including as `[]`.
       *
       * The twelve flat `casing*` keys are deliberately NOT sent alongside it.
       * When the array is present the API treats it as authoritative and
       * mirrors entries 1-2 back into those columns itself, so sending both
       * would describe two casing states in one request. That mirror is what
       * keeps every unconverted reader working until step 8 drops the columns —
       * and it is why the naming flip between them (`casingSizeInches2` in this
       * form vs `casingSize2Inches` on the wire) is now the API's problem and
       * not a per-key mapping this screen has to get right.
       *
       * Absent, the API PRESERVES what is stored — ordinals 1-2 from the
       * untouched columns, 3+ from the table. That is exactly right when this
       * form changed nothing about casing, and wrong the moment it did: if the
       * user removed the third string, the array is how the server finds out.
       */
      ...casingStringsForSave(form.casingStrings, isEditMode ? loadedCasingRef.current : null),
      threadProtectors: form.threadProtectors,
      topDriveThread: resolvedTopDrive || null,
      baleExtensions: form.baleExtensions || null,
      spider: form.spider || null,
      depthMetres: form.depthMetres || null,
      depthFeet: form.depthFeet || null,
      // { id, name } per catalog row (0368); a name the catalog does not hold
      // goes up bare for the API to resolve or refuse. null = no boxes ticked.
      servicesRequired: form.servicesRequired.length > 0 ? serviceEntriesForWrite(form.servicesRequired, serviceCatalog) : null,
      specialInstructions: form.specialInstructions || null,
    };

    if (!isEditMode) {
      payload.clonedFromJobId = clonedFromJobId || null;
      // Only when the form was opened from an opportunity AND still names that
      // opportunity's customer; a changed customer is a different deal.
      if (opportunityParam && form.customerId === customerParam) payload.opportunityId = opportunityParam;
    }

    if (isEditMode) {
      updateMutation.mutate(payload, {
        onSuccess: (res) => {
          // api#1028: the edit moved scheduled_date off the day an open run
          // was recorded as starting. The board keeps that run on its start
          // day, so say so now — a toast, because Save navigates away.
          const warning: JobEditWarning | null = res.data?.warning ?? null;
          if (warning) toast.warning(jobEditWarningLine(warning), { duration: 12_000 });
          goBack();
        },
      });
    } else {
      createMutation.mutate(payload, {
        onSuccess: (res) => {
          const newJob = res.data?.data;
          if (targetStatus === 'scheduled' && newJob?.id) {
            navigate(`/jobs/${newJob.id}/assign-crew`, { replace: true });
          } else {
            navigate('/jobs', { replace: true });
          }
        },
      });
    }
  };

  const inputClass =
    'w-full bg-fieldiq-card border border-fieldiq-border rounded-lg px-4 py-3 text-white focus:border-fieldiq-accent focus:outline-none';
  const labelClass = 'block text-sm text-white mb-1';

  return (
    <div className="animate-fade-in min-h-full pb-[var(--bottom-chrome)] bg-fieldiq-dark">
      {/* Header */}
      <div className="border-b border-border px-6 py-4">
        <button onClick={goBack} className="flex items-center gap-2 text-white hover:text-amber-400 hover:bg-white/10 text-lg font-semibold px-2 py-1 rounded transition-colors">
          <ArrowLeft size={24} /> Back
        </button>
        <div className="flex items-center justify-between mt-2">
          <h1 className="text-base font-bold text-foreground">{isEditMode ? 'Edit Job' : 'New Job'}</h1>
        </div>
      </div>

      {/* Loading state for edit mode */}
      {isEditMode && editLoading && (
        <div className="flex justify-center py-12">
          <Loader size={32} className="animate-spin text-fieldiq-accent" />
        </div>
      )}

      {!(isEditMode && editLoading) && <div className="max-w-[1920px] mx-auto px-4 py-4 space-y-3">
        {error && (
          <div className="bg-fieldiq-danger/20 border border-fieldiq-danger text-fieldiq-danger px-4 py-3 rounded-lg text-sm">
            {error}
          </div>
        )}

        {/* 1. Job Info */}
        <Section title="Job Info" defaultOpen={true}>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>
                Job Number
                {!isEditMode && <span className="ml-2 px-1.5 py-0.5 bg-fieldiq-accent/20 text-fieldiq-accent text-xs rounded font-medium">Auto</span>}
              </label>
              <input
                className={`${inputClass} opacity-60`}
                value={isEditMode ? (form.jobNumber || '—') : 'Auto-assigned on save'}
                readOnly
                tabIndex={-1}
              />
            </div>
            <div>
              <label className={labelClass}>Status</label>
              <select className={inputClass} value={form.status} onChange={(e) => set('status', e.target.value)}>
                <option value="pending">Pending</option>
                <option value="scheduled">Scheduled</option>
                <option value="active">Active</option>
                <option value="on_hold">On Hold</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
          </div>
        </Section>

        {/* 2. Client & Rig */}
        <Section title="Client & Rig">
          <div>
            <label className={labelClass}>Client</label>
            {showArchivedClient && (
              <div className="mb-2 bg-amber-50 border border-amber-300 text-amber-800 rounded px-3 py-2 text-sm">
                ⚠ The saved client "{archivedCustomer.name}" is no longer active. Please reselect before saving.
              </div>
            )}
            <select
              className={inputClass}
              value={form.customerId}
              onChange={(e) => {
                const id = e.target.value;
                if (id === '__new__') {
                  setShowNewCustomer(true);
                  return;
                }
                set('customerId', id);
                // Reset contact — will be auto-filled by useEffect when per-client contacts load
                set('customerContactName', '');
                set('customerContactPhone', '');
                set('customerContactEmail', '');
                set('customerContactRole', '');
                set('saveAsRigEmail', false);
                contactAutoFilledRef.current = null;
                setContactMode('select');
              }}
            >
              <option value="">-- Select Client --</option>
              {/* Archived client kept as the selected value so its name shows instead of a
                  blank placeholder; disabled so the user must actively reselect a live one. */}
              {showArchivedClient && (
                <option value={archivedCustomer.id} disabled>
                  {archivedCustomer.name} (archived — please reselect)
                </option>
              )}
              {/* A prospect is tagged so the office sees that booking this job
                  makes them a client (D14: every job-create door promotes). */}
              {customers.map((c: any) => (
                <option key={c.id} value={c.id}>{c.status === 'prospect' ? `${c.name} · Prospect` : c.name}</option>
              ))}
              <option value="__new__">+ Add New Client</option>
            </select>
          </div>
          {/* Inline new customer form */}
          {showNewCustomer && (
            <div className="bg-fieldiq-card border border-fieldiq-accent/40 rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-fieldiq-accent">New Client</span>
                <button
                  type="button"
                  onClick={() => { setShowNewCustomer(false); setNewCustomer({ name: '', mainContactName: '', mainContactPhone: '' }); }}
                  className="text-white hover:text-fieldiq-accent"
                >
                  <X size={16} />
                </button>
              </div>
              <div>
                <label className={labelClass}>Company Name <span className="text-fieldiq-danger">*</span></label>
                <input
                  className={inputClass}
                  placeholder="e.g. Husky Energy"
                  value={newCustomer.name}
                  onChange={(e) => setNewCustomer((p) => ({ ...p, name: e.target.value }))}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelClass}>Contact Name</label>
                  <input
                    className={inputClass}
                    placeholder="Main contact"
                    value={newCustomer.mainContactName}
                    onChange={(e) => setNewCustomer((p) => ({ ...p, mainContactName: e.target.value }))}
                  />
                </div>
                <div>
                  <label className={labelClass}>Contact Phone</label>
                  <input
                    type="tel"
                    className={inputClass}
                    placeholder="(780)555-0100"
                    value={newCustomer.mainContactPhone}
                    onChange={(e) => setNewCustomer((p) => ({ ...p, mainContactPhone: formatPhoneNumber(e.target.value) }))}
                  />
                </div>
              </div>
              <button
                type="button"
                onClick={handleSaveNewCustomer}
                disabled={createCustomerMutation.isPending}
                className="w-full bg-fieldiq-accent text-fieldiq-dark font-bold py-2.5 rounded-lg disabled:opacity-50 flex items-center justify-center gap-2 hover:bg-amber-600 transition-colors text-sm"
              >
                {createCustomerMutation.isPending ? (
                  <><Loader size={16} className="animate-spin" /> Saving...</>
                ) : (
                  <><Plus size={16} /> Save Client</>
                )}
              </button>
            </div>
          )}
          {/* Rate sheet — which book prices this job.
              Sits with the client because it depends on one: the options are
              that client's contracts plus the company-wide books. */}
          <div>
            <label className={labelClass}>Rate Sheet</label>
            <select
              // This form's <label> elements carry no htmlFor and are not
              // associated with their controls, so without this the select has
              // no accessible name at all — to a screen reader as much as to
              // anything else that asks. Same reason RateSheetItemPicker takes
              // an `ariaLabel`.
              aria-label="Rate Sheet"
              className={inputClass}
              value={form.rateSheetId}
              onChange={(e) => set('rateSheetId', e.target.value)}
              disabled={!form.customerId}
            >
              {/* "Not set" is a real, preferred answer, not a prompt to choose.
                  Most jobs should leave it here: the API resolves the sheet and
                  says so when it cannot. Only pick when the books genuinely
                  disagree for this job. */}
              <option value="">
                {inheritedRateSheetLabel(rateSheets as any[], customerDefaultRateSheetId)}
              </option>
              {clientSheets.length > 0 && (
                <optgroup label="This client's rates">
                  {clientSheets.map((rs: any) => (
                    <option key={rs.id} value={rs.id}>{rs.name}</option>
                  ))}
                </optgroup>
              )}
              {companyBooks.length > 0 && (
                <optgroup label="Company price books">
                  {companyBooks.map((rs: any) => (
                    <option key={rs.id} value={rs.id}>{rs.name}</option>
                  ))}
                </optgroup>
              )}
            </select>
            {!form.customerId && (
              <p className="text-xs text-fieldiq-muted mt-1">Select a client first.</p>
            )}
            {form.customerId && companyBooks.length > 1 && !form.rateSheetId && (
              // Only shown where it is actionable. A tenant with one book has
              // nothing to decide, and saying so anyway trains people to ignore
              // the line.
              <p className="text-xs text-amber-500 mt-1">
                {companyBooks.length} company price books exist — if they price this job
                differently, set it here or the ticket draft will come back unpriced.
              </p>
            )}
          </div>

          {/* Rig Name with autocomplete */}
          <div className="relative" ref={rigDropdownRef}>
            <label className={labelClass}>Rig Name</label>
            <div className="relative">
              <input
                className={inputClass + (form.rigName ? ' pr-8' : '')}
                placeholder="e.g. Rig 14"
                value={form.rigName}
                onChange={(e) => {
                  changeRigFromUser(e.target.value);
                  setShowDuplicatePrompt(false);
                  setRigSourceJob(null);
                  setClonedFromJobId(null);
                }}
                onFocus={() => setRigInputFocused(true)}
                autoComplete="off"
              />
              {form.rigName && (
                <button
                  type="button"
                  onClick={() => {
                    changeRigFromUser('');
                    setShowDuplicatePrompt(false);
                    setRigSourceJob(null);
                    setClonedFromJobId(null);
                    setRigInputFocused(true);
                  }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-fieldiq-muted hover:text-white transition-colors"
                >
                  <X size={14} />
                </button>
              )}
            </div>
            {/* Autocomplete dropdown */}
            {rigInputFocused && (filteredRigs.length > 0 || form.rigName.trim()) && (
              <div className="absolute z-50 left-0 right-0 mt-1 bg-fieldiq-card border border-fieldiq-border rounded-lg shadow-xl max-h-48 overflow-y-auto">
                {filteredRigs.map((rig) => (
                  <button
                    key={rig}
                    type="button"
                    className="w-full text-left px-4 py-2.5 text-sm text-white hover:bg-fieldiq-border transition-colors"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      handleRigSelect(rig);
                    }}
                  >
                    {rig}
                  </button>
                ))}
                <button
                  type="button"
                  className="w-full text-left px-4 py-2.5 text-sm text-fieldiq-accent font-medium hover:bg-fieldiq-border transition-colors flex items-center gap-2 border-t border-fieldiq-border"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    setRigInputFocused(false);
                  }}
                >
                  <Plus size={14} />
                  {form.rigName.trim() && !allRigs.some((r) => r.toLowerCase() === form.rigName.trim().toLowerCase())
                    ? `Add New: ${form.rigName.trim()}`
                    : 'Add New Rig'}
                </button>
              </div>
            )}
          </div>
          {/* Duplicate prompt banner */}
          {showDuplicatePrompt && rigSourceJob && (
            <div className="bg-amber-500/10 border border-amber-500/40 rounded-lg px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <p className="text-sm text-amber-400">
                Previous job found for <span className="font-semibold">{rigSourceJob.rigName}</span> (#{rigSourceJob.jobNumber})
              </p>
              <div className="flex flex-col sm:flex-row gap-2 sm:flex-shrink-0">
                <button
                  type="button"
                  onClick={handleDuplicate}
                  className="flex items-center justify-center gap-1.5 px-3 py-3 sm:py-1.5 text-sm font-medium bg-amber-500 text-fieldiq-dark rounded-lg hover:bg-amber-600 transition-colors"
                >
                  <Copy size={14} />
                  Duplicate Last Job
                </button>
                <button
                  type="button"
                  onClick={handleCreateNew}
                  className="flex items-center justify-center gap-1.5 px-3 py-3 sm:py-1.5 text-sm font-medium border border-fieldiq-border text-white rounded-lg hover:border-fieldiq-accent transition-colors"
                >
                  <Plus size={14} />
                  Create New
                </button>
              </div>
            </div>
          )}
          {!showNewCustomer && (
            <div className="space-y-3">
              {/* Contact selector (rig-scoped) */}
              <div className="relative" ref={contactDropdownRef}>
                <label className={labelClass}>Contact</label>
                {contactMode === 'select' ? (
                  <>
                    <button
                      type="button"
                      onClick={() => setContactDropdownOpen(!contactDropdownOpen)}
                      className={`${inputClass} text-left flex items-center justify-between`}
                    >
                      {/*
                        NAME-OR-EMAIL, not name alone. A rig whose several
                        contacts share one mailbox auto-fills the ADDRESS and
                        deliberately leaves the NAME blank — the rows disagree
                        on it, and putting one of three real hands onto the job
                        would invent a fact. Keyed on the name, this trigger
                        would then render "-- Select Contact --" over a
                        populated, about-to-be-saved address: a value captured
                        and displayed nowhere, which is the exact bug
                        NewJobScreen.contact-email-visible.test.tsx exists for.
                        The name renders as an em dash when it is the absent
                        half, per the same rule.
                      */}
                      {(form.customerContactName || form.customerContactEmail) ? (
                        <span className="flex flex-col min-w-0 text-white">
                          <span className="truncate">
                            {form.customerContactName || EM_DASH}
                            {form.customerContactPhone ? ` — ${form.customerContactPhone}` : ''}
                          </span>
                          <span className="flex items-center gap-1.5 text-xs text-fieldiq-text">
                            <Mail size={11} className="flex-shrink-0" />
                            <span className="truncate">{contactEmailLabel(form.customerContactEmail)}</span>
                          </span>
                        </span>
                      ) : (
                        <span className="text-fieldiq-muted">-- Select Contact --</span>
                      )}
                      <ChevronDown size={16} className="text-fieldiq-text flex-shrink-0" />
                    </button>
                    {contactDropdownOpen && (
                      <div className="absolute z-50 left-0 right-0 mt-1 bg-fieldiq-card border border-fieldiq-border rounded-lg shadow-xl max-h-48 overflow-y-auto">
                        {clientContacts.length === 0 && (
                          <div className="px-4 py-2.5 text-sm text-fieldiq-muted">
                            {form.rigName ? 'No saved contacts for this rig yet' : 'No contacts for this client'}
                          </div>
                        )}
                        {clientContacts.map((ct) => (
                          <button
                            key={ct.id}
                            type="button"
                            className="w-full text-left px-4 py-2.5 text-sm text-white hover:bg-fieldiq-border transition-colors flex flex-col gap-0.5"
                            onMouseDown={(e) => {
                              e.preventDefault();
                              set('customerContactName', ct.name);
                              set('customerContactPhone', ct.phone || '');
                              set('customerContactEmail', ct.email || '');
                              set('customerContactRole', ct.role || '');
                              // The box asserts something about the address in
                              // the field. Picking a different contact replaces
                              // that address, so a tick made for the previous
                              // one must not carry over to this one.
                              set('saveAsRigEmail', false);
                              setContactDropdownOpen(false);
                            }}
                          >
                            <span className="flex items-center gap-2 w-full min-w-0">
                              <Phone size={14} className="text-fieldiq-text flex-shrink-0" />
                              <span className="truncate">{ct.name}</span>

                              {ct.phone && (
                                <span className="text-fieldiq-text ml-auto flex-shrink-0">{ct.phone}</span>
                              )}
                            </span>
                            <span className="flex items-center gap-2 w-full min-w-0 text-xs text-fieldiq-text">
                              <Mail size={14} className="flex-shrink-0" />
                              <span className="truncate">{contactEmailLabel(ct.email)}</span>
                            </span>
                          </button>
                        ))}
                        <button
                          type="button"
                          className="w-full text-left px-4 py-2.5 text-sm text-fieldiq-accent font-medium hover:bg-fieldiq-border transition-colors flex items-center gap-2"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            setContactMode('new');
                            set('customerContactName', '');
                            set('customerContactPhone', '');
                            set('customerContactEmail', '');
                            set('customerContactRole', '');
                            set('saveAsRigEmail', false);
                            setContactDropdownOpen(false);
                          }}
                        >
                          <Plus size={14} />
                          Add New Contact
                        </button>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className={labelClass}>Contact Name</label>
                        <input
                          className={inputClass}
                          placeholder="Contact name"
                          value={form.customerContactName}
                          onChange={(e) => set('customerContactName', e.target.value)}
                        />
                      </div>
                      <div>
                        <label className={labelClass}>Contact Phone</label>
                        <input
                          type="tel"
                          className={inputClass}
                          placeholder="(780)555-0100"
                          value={form.customerContactPhone}
                          onChange={(e) => set('customerContactPhone', formatPhoneNumber(e.target.value))}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className={labelClass}>Contact Email</label>
                        <input
                          type="email"
                          className={inputClass}
                          placeholder="consultant@example.com"
                          value={form.customerContactEmail}
                          onChange={(e) => set('customerContactEmail', e.target.value)}
                        />
                      </div>
                      {/*
                        Beside the address on purpose. The role is recorded at
                        the moment the office knows who the person is — while
                        they are typing the contact — not asked for later by
                        someone who was not on the call.

                        It is RECORDED, NOT CONSULTED. Nothing resolves on role:
                        the approval chain reads the job's own columns and never
                        looks at customer_contacts. Blank is a perfectly good
                        answer and means ELIGIBLE, not unendorsed — every one of
                        the estate's 49 rig-scoped contacts was blank when this
                        shipped. So there is no required marker and no
                        validation; leaving it alone costs nothing.
                      */}
                      <div>
                        {/*
                          htmlFor/id, unlike its siblings in this form. A bare
                          <label> beside a <select> is not associated with it,
                          so the control has no accessible name — a screen
                          reader reads "combo box" and nothing else. The
                          neighbours predate this and are not changed here;
                          new markup should not inherit the fault.
                        */}
                        <label className={labelClass} htmlFor="customer-contact-role">Role</label>
                        <select
                          id="customer-contact-role"
                          className={inputClass}
                          value={form.customerContactRole}
                          onChange={(e) => set('customerContactRole', e.target.value)}
                        >
                          {CONTACT_ROLE_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    {/*
                      "Use this address for this job's approval emails" — the
                      ticket send modal's label since fe#859. One column, one
                      vocabulary: `jobs.rig_approval_email` should not acquire
                      a second name because it grew a second door.

                      Gated on a rig name for the same reason the send modal
                      gates its own on `sendRigName` — the helper names the rig,
                      and without a rig the field it writes has no meaning.

                      BOTH DOORS WRITE ONE JOB. Since api#841 (A3) the send
                      modal's `saveRigEmail` writes that ticket's job and no
                      other; it used to write every job on the rig, and the
                      old "preferred email for {rig}" label promised that
                      rig-wide default on both screens.

                      The helper's second sentence DIFFERS from the modal's on
                      purpose. The modal offers an explicit "save the contact
                      below"; this form has no such control — the API's
                      upsertJobContact saves the typed contact into
                      customer_contacts on every job save, and only when it has
                      a name. So the sentence says that instead.

                      AND IT IS REACHABLE ONLY FROM THIS BRANCH. Contact Email
                      renders only under `contactMode === 'new'`, so picking a
                      SAVED contact from the dropdown never reveals this box.
                      That is a visibility limit, filed as "Limit 2", and it is
                      probably right as it stands: a saved contact scoped to a
                      rig has ALREADY recorded that rig's address — that is what
                      `customer_contacts.rig_name` IS — so this box is for the
                      case where the office is typing an address nothing has
                      seen before. Do NOT add a second checkbox to the select
                      branch; that gives one column a third door. The coherent
                      version of that change is a different one, and the filing
                      says which.
                    */}
                    {form.rigName.trim() && (
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          className="w-4 h-4 rounded"
                          checked={form.saveAsRigEmail}
                          onChange={(e) => set('saveAsRigEmail', e.target.checked)}
                          disabled={!form.customerContactEmail.trim()}
                        />
                        <span className="text-xs text-fieldiq-text">
                          Use this address for this job's approval emails
                        </span>
                      </label>
                    )}
                    {form.rigName.trim() && (
                      <p className="-mt-1 ml-6 text-xs text-fieldiq-muted">
                        Other jobs on {form.rigName.trim()} aren't changed. Saving
                        the job also saves this contact, if it has a name, for
                        reuse on new jobs.
                      </p>
                    )}
                    {clientContacts.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setContactMode('select')}
                        className="text-xs text-fieldiq-accent hover:underline"
                      >
                        Choose from saved contacts
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </Section>

        {/* 3. Location (now includes Well Name & Well ID) */}
        <Section title="Location">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>Well Name</label>
              <input className={inputClass} placeholder="e.g. Pembina 7-23" value={form.wellName} onChange={(e) => set('wellName', e.target.value)} />
            </div>
            <div>
              <label className={labelClass}>Well ID / LSD</label>
              {/* Zero-padded, and offering a free-text alternative — the same
                  two things the LSD example on the field ticket editor says.
                  This was the only one of the app's three LSD examples written
                  short, and an example is what a form teaches: 31 of the 54
                  live rows in this column carry a spelling that is not the
                  stored one. The API canonicalizes a recognised identifier on
                  save (api #674) and stores anything else exactly as typed,
                  which is why 'Pad 21' belongs beside it rather than reading
                  as a value the form would reject. */}
              <input className={inputClass} placeholder="e.g. 07-23-048-07W5 or Pad 21" value={form.wellIdLsd} onChange={(e) => set('wellIdLsd', e.target.value)} />
            </div>
          </div>
          {/* AFE and Well Licence. The column has existed on `jobs` since before
              this form did and was populated on 0 of 230 rows across every
              tenant — because there was no input for it, not because nobody
              wants one.

              These are the office DEFAULT. Every ticket created on this job
              inherits them, which is the whole answer for a single-well job (32
              of TNT's 42, and effectively every job on every other tenant). On a
              completions pad the job spans wells and the hand corrects the AFE
              per ticket on the field screen — an AFE is per WELL, and one job
              can be seven of them. */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>AFE / PO #</label>
              <input className={inputClass} maxLength={100} placeholder="e.g. CADR260057" value={form.afeNumber} onChange={(e) => set('afeNumber', e.target.value)} />
            </div>
            <div>
              <label className={labelClass}>Well Licence</label>
              <input className={inputClass} maxLength={50} placeholder="Licence / WA number" value={form.wellLicence} onChange={(e) => set('wellLicence', e.target.value)} />
            </div>
          </div>
          <div>
            <label className={labelClass}>Location Type</label>
            <ToggleGroup
              options={[
                { value: 'surface', label: 'Surface' },
                { value: 'bottom', label: 'Bottom' },
              ]}
              value={form.locationType}
              onChange={(v) => set('locationType', v)}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>City / Area</label>
              <input className={inputClass} placeholder="e.g. Drayton Valley" value={form.locationCity} onChange={(e) => set('locationCity', e.target.value)} />
            </div>
            <div>
              <label className={labelClass}>Province</label>
              <ProvinceSelect value={form.locationProvinceState} onChange={(v) => set('locationProvinceState', v)} className={inputClass} />
            </div>
          </div>
          <div>
            <label className={labelClass}>Directions</label>
            <textarea className={inputClass} rows={3} placeholder="Directions to location..." value={form.directions} onChange={(e) => set('directions', e.target.value)} />
          </div>
          <div>
            <label className={labelClass}>Radio Frequency</label>
            <input className={inputClass} placeholder="e.g. 148.840" value={form.radioFrequency} onChange={(e) => set('radioFrequency', e.target.value)} />
          </div>
        </Section>

        {/* 4. Schedule */}
        <Section title="Schedule">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>Scheduled Date</label>
              <DatePicker value={form.scheduledDate} onChange={(v) => set('scheduledDate', v)} />
              {isEditMode && editJobData && (
                <PhaseStartNotice
                  jobId={jobId!}
                  jobNumber={form.jobNumber || undefined}
                  phase="run"
                  startedAt={editJobData.run_started_at ?? editJobData.runStartedAt}
                  endedAt={editJobData.run_ended_at ?? editJobData.runEndedAt}
                />
              )}
            </div>
            <div>
              {/* Same name the read surfaces use for this column — see the
                  note above the On Location field in pages/jobs/JobDetail.tsx.
                  It pairs with "Scheduled Date" above rather than matching it
                  because the DAY and the TIME are asked as two questions, and
                  only the time has an operational name. */}
              <label className={labelClass}>On Location</label>
              <TimePicker value={form.scheduledTime} onChange={(v) => set('scheduledTime', v)} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>Wash Date</label>
              <DatePicker value={form.dateTimeWash} onChange={(v) => set('dateTimeWash', v)} />
              {isEditMode && editJobData && (
                <PhaseStartNotice
                  jobId={jobId!}
                  jobNumber={form.jobNumber || undefined}
                  phase="wash"
                  startedAt={editJobData.wash_started_at ?? editJobData.washStartedAt}
                  endedAt={editJobData.wash_ended_at ?? editJobData.washEndedAt}
                />
              )}
            </div>
            <div>
              <label className={labelClass}>Wash Time</label>
              <TimePicker value={form.washTime} onChange={(v) => set('washTime', v)} />
            </div>
          </div>
        </Section>

        {/* 5. Job Type */}
        <Section title="Job Type">
          <RadioGroup
            options={JOB_TYPE_OPTIONS}
            value={form.jobType}
            onChange={(v) => set('jobType', v)}
            columns={3}
          />
        </Section>

        {/* 6-8. Casing / Tubing Strings — one section per string, N of them */}
        <Section title="Casing / Tubing Strings">
          {/*
            One CasingStringSection per string. Each owns its own pipe-type
            toggle, size/weight/grade/thread pickers, cascades, size+thread
            canonicalization and torque window — see that file's header for why
            the canonicalization must not move back up here.

            Keyed on `s.key`, NOT on the ordinal or the array index: both change
            under a string when an earlier one is removed, and React would then
            reuse the removed section's state for its neighbour.
          */}
          {form.casingStrings.map((s, i) => (
            <CasingStringSection
              key={s.key}
              value={s}
              // The STORED number, never the position — see removeCasingString.
              stringNumber={s.stringNumber ?? i + 1}
              onChange={(patch) => updateCasingString(s.key, patch)}
              // String 1 gets no Remove control. A job with no casing strings is
              // not a valid casing job.
              onRemove={i === 0 ? undefined : () => removeCasingString(s.key)}
            />
          ))}

          {form.casingStrings.length < MAX_CASING_STRINGS && (
            <button
              type="button"
              onClick={addCasingString}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 border border-dashed border-fieldiq-border rounded-lg text-sm text-fieldiq-accent font-medium hover:border-fieldiq-accent transition-colors"
            >
              <Plus size={16} />
              Add String
            </button>
          )}
        </Section>

        {/* 10. Thread Protectors */}
        <Section title="Thread Protectors">
          <ToggleGroup
            options={[
              { value: 'yes', label: 'Yes' },
              { value: 'no', label: 'No' },
            ]}
            value={form.threadProtectors ? 'yes' : 'no'}
            onChange={(v) => set('threadProtectors', v === 'yes')}
          />
        </Section>

        {/* 11. Top Drive Connection */}
        <Section title="Top Drive Connection">
          <TopDriveThreadSelect
            value={form.topDriveThread}
            onChange={(v) => set('topDriveThread', v)}
          />
        </Section>

        {/* 12. Bale Extensions */}
        <Section title="Bale Extensions">
          <RadioGroup
            options={BALE_OPTIONS}
            value={form.baleExtensions}
            onChange={(v) => set('baleExtensions', v)}
            columns={3}
          />
        </Section>

        {/* 12b. Spider (surface jobs only) */}
        {form.jobType === 'surface' && (
          <Section title="Spider">
            <RadioGroup
              options={[
                { value: 'yes', label: 'Yes' },
                { value: 'no', label: 'No' },
              ]}
              value={form.spider}
              onChange={(v) => set('spider', v)}
              columns={2}
            />
          </Section>
        )}

        {/* 13. Depth */}
        <Section title="Depth / Metres to Run">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>Metres</label>
              <input
                type="number"
                step="0.1"
                className={inputClass}
                placeholder="e.g. 1500"
                value={form.depthMetres}
                onChange={(e) => set('depthMetres', e.target.value)}
              />
            </div>
            <div>
              <label className={labelClass}>Feet (auto)</label>
              <input className={`${inputClass} opacity-60`} value={form.depthFeet} readOnly tabIndex={-1} />
            </div>
          </div>
        </Section>

        {/* 14. Services Required */}
        <Section title="Services Required">
          {/* In flight: the section is disabled rather than empty. An empty
              checkbox list reads as "this tenant offers no services", and a
              save made against that view would wipe the job's selections. */}
          {servicesLoading && (
            <div className="flex items-center gap-2 px-3 py-2.5 text-sm text-fieldiq-text">
              <Loader className="w-4 h-4 animate-spin" />
              Loading services…
            </div>
          )}

          {/* Fetch failed: the job's own saved values are still editable — they
              come from the form, not the catalog — but no new service can be
              added until the catalog is back. */}
          {servicesError && (
            <div className="flex items-center justify-between gap-3 px-3 py-2.5 mb-2 rounded-lg border border-amber-500/40 bg-amber-500/10 text-sm">
              <span className="text-amber-300">
                Couldn't load the service list. Showing this job's saved services only.
              </span>
              <button
                type="button"
                onClick={() => refetchServices()}
                disabled={servicesFetching}
                className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-amber-500/50 text-amber-300 hover:text-white disabled:opacity-50 flex-shrink-0"
              >
                {servicesFetching ? 'Retrying…' : 'Retry'}
              </button>
            </div>
          )}

          <div className="grid grid-cols-1 gap-2">
            {visibleServices.map((svc) => {
              const checked = form.servicesRequired.includes(svc);
              /* Stored on the job but absent from the active catalog — either
                 legacy vocabulary or a service since deactivated. Greyed and
                 titled, but NOT disabled: unchecking it is the one edit that
                 has to keep working. */
              const retired = catalogLoaded && !catalogServices.includes(svc);
              return (
                <Tooltip key={svc} content={retired ? 'No longer offered' : undefined} className="w-full">
                  <button
                    type="button"
                    disabled={servicesLoading}
                    onClick={() => {
                      set(
                        'servicesRequired',
                        checked
                          ? form.servicesRequired.filter((s) => s !== svc)
                          : [...form.servicesRequired, svc]
                      );
                    }}
                    className={`flex w-full items-center gap-2 px-3 py-2.5 rounded-lg border text-sm text-left transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                      checked
                        ? 'border-fieldiq-accent bg-amber-500/10'
                        : 'border-fieldiq-border bg-fieldiq-card hover:border-fieldiq-accent'
                    } ${
                      retired
                        ? 'text-fieldiq-muted'
                        : checked
                          ? 'text-fieldiq-accent'
                          : 'text-white'
                    }`}
                  >
                    <span className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${
                      checked ? 'bg-fieldiq-accent border-fieldiq-accent' : 'border-fieldiq-border'
                    }`}>
                      {checked && <span className="text-fieldiq-dark text-xs font-bold">✓</span>}
                    </span>
                    {svc}
                    {retired && (
                      <span className="ml-auto text-[11px] uppercase tracking-wide text-fieldiq-muted flex-shrink-0">
                        No longer offered
                      </span>
                    )}
                  </button>
                </Tooltip>
              );
            })}

            {/* Catalog loaded and genuinely empty — say so, don't imply a load
                is still pending. */}
            {catalogLoaded && visibleServices.length === 0 && (
              <p className="px-3 py-2.5 text-sm text-fieldiq-muted">
                No services configured. Add them under Settings → Services.
              </p>
            )}
          </div>
        </Section>

        {/* 15. Special Instructions */}
        <Section title="Special Instructions">
          <textarea
            className={inputClass}
            rows={4}
            placeholder="Any special instructions for the crew..."
            value={form.specialInstructions}
            onChange={(e) => set('specialInstructions', e.target.value)}
          />
        </Section>
      </div>}

      {/* Bottom action buttons — scrolls with content.
          On mobile the last button must clear the fixed AI-agent FAB, which sits at
          var(--fab-bottom) and is 72px tall; +8px breathing room above it. */}
      {!(isEditMode && editLoading) && <div className="border-t border-fieldiq-border px-4 pt-4 pb-[calc(var(--fab-bottom)+80px)] mt-4 sm:pb-4">
        <div className="max-w-[1920px] mx-auto flex gap-3">
          {isEditMode ? (
            <button
              type="button"
              onClick={() => handleSave()}
              disabled={activeMutation.isPending}
              className="flex-1 bg-fieldiq-accent text-fieldiq-dark font-bold py-3 rounded-lg disabled:opacity-50 flex items-center justify-center gap-2 hover:bg-amber-600 transition-colors"
            >
              {activeMutation.isPending ? (
                <><Loader size={18} className="animate-spin" /> Saving...</>
              ) : (
                'Save Changes'
              )}
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={() => handleSave('pending')}
                disabled={activeMutation.isPending}
                className="flex-1 bg-fieldiq-card border border-fieldiq-border text-white font-bold py-3 rounded-lg disabled:opacity-50 flex items-center justify-center gap-2 hover:bg-fieldiq-border transition-colors"
              >
                {activeMutation.isPending ? (
                  <><Loader size={18} className="animate-spin" /> Saving...</>
                ) : (
                  'Save as Pending'
                )}
              </button>
              <button
                type="button"
                onClick={() => handleSave('scheduled')}
                disabled={activeMutation.isPending}
                className="flex-1 bg-fieldiq-accent text-fieldiq-dark font-bold py-3 rounded-lg disabled:opacity-50 flex items-center justify-center gap-2 hover:bg-amber-600 transition-colors"
              >
                {activeMutation.isPending ? (
                  <><Loader size={18} className="animate-spin" /> Saving...</>
                ) : (
                  'Save & Assign Crew →'
                )}
              </button>
            </>
          )}
        </div>
      </div>}
    </div>
  );
}
